import { Button, buttonVariants } from "@/components/ui/button";
import * as React from "react";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchEmployees } from "@/services/getEmployees";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { Role } from "@/core/auth/models/role.model";
import withAuthorization from "@/core/auth/components/withAuthorization";
import { updateEmployee } from "@/services/updateEmployee";
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { deleteEmployee } from "@/services/deleteEmployee";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Link } from "react-router-dom";
import { Eye, Plus } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import RoleGuard from "@/core/auth/components/role-guard";
import { Skeleton } from "@/components/ui/skeleton";

function EmployeesTable() {
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [currentEmployee, setCurrentEmployee] = React.useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [employeeToDelete, setEmployeeToDelete] = React.useState<any>(null);

  const { user } = useAuth();
  const queryClient = useQueryClient();

  const EmployeeData = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const data = await fetchEmployees();
      return data.map((employee: any) => {
        return { ...employee, company: employee.company?.name };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const mutation = useMutation({
    mutationFn: (updatedEmployee: any) =>
      updateEmployee(updatedEmployee._id, updatedEmployee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      toast.success("Employee updated successfully");
      setIsEditModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleEditClick = (employee: any) => {
    setCurrentEmployee(employee);
    setIsEditModalOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: (employeeId: string) => deleteEmployee(employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      toast.success("Employee deleted successfully");
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      toast.error("Error deleting employee: " + error.message);
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentEmployee((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeleteClick = (employee: any) => {
    setEmployeeToDelete(employee);
    setIsDeleteModalOpen(true);
  };
  // Manual table rendering, no react-table
  return (
    <div className="p-6 w-full mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-8">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Employees</h1>
          <p className="text-muted-foreground">
            Manage your team members and their roles
          </p>
        </div>
        <RoleGuard requiredRoles={[Role.CompanyAdmin, Role.SuperAdmin]}>
          <Link to="/app/add-employee">
            <Button variant="default" className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Employee
            </Button>
          </Link>
        </RoleGuard>
      </div>

      {/* Main Content */}
      <div className="md:p-4">
        {EmployeeData.isPending ? (
          [1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center space-x-4 w-full my-8">
                <div className="space-y-2 w-full">
                  <Skeleton className="h-4 w-[100%]" />
                  <Skeleton className="h-4 w-[100%]" />
                </div>
              </div>
            ))
        ) : EmployeeData.isError ? (
          <div>Error: {EmployeeData.error.message}</div>
        ) : (
          <>
            <ScrollArea className="rounded-md border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.isArray(EmployeeData.data) && EmployeeData.data.length > 0 ? (
                    EmployeeData.data.map((employee: any) => (
                      <TableRow key={employee._id}>
                        <TableCell>{employee.name}</TableCell>
                        <TableCell>{employee.email || "-"}</TableCell>
                        <TableCell className="capitalize">{employee.role || "-"}</TableCell>
                        <TableCell>{employee.phoneNumber || "-"}</TableCell>
                        <TableCell>{employee.company || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Link to={`/app/employees/${employee._id}`}>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="border-none"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </Link>
                                </TooltipTrigger>
                                <TooltipContent>View employee</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center">
                        No results.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogOverlay />
          <DialogContent className="w-96">
            <DialogTitle>Edit Employee</DialogTitle>
            <div className="grid grid-cols-1 gap-4 w-full">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={currentEmployee?.name || ""}
                  onChange={(event) =>
                    setCurrentEmployee({
                      ...currentEmployee,
                      name: event.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={currentEmployee?.email || ""}
                  onChange={(event) =>
                    setCurrentEmployee({
                      ...currentEmployee,
                      email: event.target.value,
                    })
                  }
                />
              </div>
              <div className="flex justify-end w-full gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    mutation.mutate({
                      _id: currentEmployee._id,
                      name: currentEmployee.name,
                      email: currentEmployee.email,
                    })
                  }
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && (
        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogOverlay />
          <DialogContent className="w-96">
            <DialogTitle>Delete Employee</DialogTitle>
            <div className="space-y-4">
              <p>Are you sure you want to delete this employee?</p>
              <div className="flex justify-end w-full gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (employeeToDelete) {
                      deleteMutation.mutate(employeeToDelete._id);
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const EmployeesPage = withAuthorization(EmployeesTable, [
  Role.SuperAdmin,
  Role.CompanyAdmin,
  Role.Employee,
]);

export default EmployeesPage;
